import { useState } from 'react';
import API from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Upload, CheckCircle, AlertCircle, Loader2, FileSpreadsheet, Eye } from 'lucide-react';

export default function AdminImport() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handlePreview = async () => {
    if (!file) return;
    setPreviewing(true);
    setError('');
    setPreview(null);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await API.post('/admin/import-preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Preview failed');
    }
    setPreviewing(false);
  };

  const handleCommit = async () => {
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await API.post('/admin/import-voters', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(data);
      setPreview(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Import failed');
    }
    setUploading(false);
  };

  return (
    <div data-testid="admin-import-page">
      <h1 className="font-heading text-2xl font-bold text-slate-900 mb-6">Import Voters</h1>

      <Card className="border-slate-200 max-w-3xl">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-slate-500" />
            Upload CSV File
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center">
            <Upload className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <input
              data-testid="import-file-input"
              type="file"
              accept=".csv"
              onChange={(e) => { setFile(e.target.files[0]); setPreview(null); setResult(null); }}
              className="block w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
            />
          </div>

          <div className="text-xs text-slate-500">
            <p className="font-semibold mb-1">Expected columns:</p>
            <code className="bg-slate-100 px-2 py-1 rounded text-xs">No, Voter's Name, Precinct, City/Municipality, Province</code>
          </div>

          <div className="flex gap-2">
            <Button
              data-testid="import-preview-btn"
              onClick={handlePreview}
              disabled={!file || previewing}
              variant="outline"
              className="flex-1"
            >
              {previewing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
              {previewing ? 'Analyzing...' : 'Preview'}
            </Button>
            <Button
              data-testid="import-commit-btn"
              onClick={handleCommit}
              disabled={!file || uploading || !preview}
              className="flex-1 bg-slate-900 hover:bg-slate-800"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              {uploading ? 'Importing...' : 'Commit Import'}
            </Button>
          </div>

          {error && (
            <div data-testid="import-error" className="flex items-center gap-2 bg-red-50 text-red-700 px-4 py-3 rounded-lg">
              <AlertCircle className="w-5 h-5" /> {error}
            </div>
          )}

          {preview && (
            <div data-testid="import-preview" className="space-y-3">
              <div className="grid grid-cols-3 gap-3 text-center bg-slate-50 rounded-lg p-4">
                <div>
                  <p className="text-2xl font-bold text-slate-900">{preview.total}</p>
                  <p className="text-xs text-slate-500">Total Records</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-600">{preview.new_count}</p>
                  <p className="text-xs text-slate-500">New Voters</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">{preview.duplicate_count}</p>
                  <p className="text-xs text-slate-500">Duplicates</p>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full data-table text-xs">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Voter's Name</th>
                      <th>Precinct</th>
                      <th>City/Municipality</th>
                      <th>Province</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.preview.map((r, i) => (
                      <tr key={i}>
                        <td>{r.row}</td>
                        <td className="font-medium">{r.full_name}</td>
                        <td className="font-mono">{r.precinct}</td>
                        <td>{r.city_municipality}</td>
                        <td>{r.province}</td>
                        <td>
                          <Badge className={`text-xs ${r.is_duplicate ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
                            {r.is_duplicate ? 'Duplicate' : 'New'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {result && (
            <div data-testid="import-result" className="bg-emerald-50 rounded-lg p-4">
              <div className="flex items-center gap-2 text-emerald-700 font-semibold mb-2">
                <CheckCircle className="w-5 h-5" /> Import Complete
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-2xl font-bold text-slate-900">{result.total}</p>
                  <p className="text-xs text-slate-500">Total Records</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-600">{result.new}</p>
                  <p className="text-xs text-slate-500">New Voters</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">{result.duplicates}</p>
                  <p className="text-xs text-slate-500">Duplicates</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
